import React, {
  useCallback,
  useContext,
  useEffect, useMemo,
  useRef,
  useState,
} from 'react';
import Button from '../../../GbsUI/UI/Button';
import Checkbox from '../../../GbsUI/UI/Checkbox';
import ArrowLeftIcon from '../../../GbsUI/UI/icons/ArrowLeft';
import ArrowRightIcon from '../../../GbsUI/UI/icons/ArrowRight';
import InputLabel from '../../../GbsUI/UI/InputLabel';
import Radio from '../../../GbsUI/UI/Radio';
import Select from '../../../GbsUI/UI/Select';
import TextInput from '../../../GbsUI/UI/TextInput';
import { useTaxonomy } from '../../../hooks/useTaxonomy';
import SignupLayout from '../components/Layout';
import { SignupContext } from '../context';
import stepThreeSchema from '../schema/step-three';

const SignupStepSeven = ({ history }) => {
  const { state, setState } = useContext(SignupContext);
  const [formErrors, setFormErrors] = useState(new Map());
  const initialized = useRef(false);
  const selectedSat = useMemo(() => {
    if (state?.testScore?.sat1600?.enabled) {
      return 'sat1600';
    } else if (state?.testScore?.sat2400?.enabled) {
      return 'sat2400';
    } else if (state?.testScore?.act?.enabled) {
      return 'act';
    } else if (state?.testScore?.na?.enabled) {
      return 'na';
    }

    return '';
  }, [state]);
  const { data: act, isLoading: isActLoading } = useTaxonomy('ACT');
  const { data: sat, isLoading: isSatLoading } = useTaxonomy(
    'SAT',
    {
      v: selectedSat,
    },
    {
      math: [],
      ...(selectedSat === 'sat2400' ? {
        reading: [],
        writing: [],
      } : {
        readingAndWriting: []
      })
    }
  );
  const { data: gre, isLoading: isGreLoading } = useTaxonomy(
    'GRE',
    {},
    {
      verbal: [],
      quantitative: [],
      writing: [],
    }
  );
  const { data: gmat, isLoading: isGmatLoading } = useTaxonomy('GMAT');
  const { data: lsat, isLoading: isLsatLoading } = useTaxonomy('LSAT');

  const validate = useCallback((data, abortEarly = false) => {
    return stepThreeSchema
      .validate(data, { abortEarly })
      .then(data => {
        setFormErrors(new Map());
        return data;
      })
      .catch(errors => {
        if (errors.inner.length > 0) {
          const errorsMap = errors.inner.reduce((accum, item) => {
            accum.set(item.path, item.errors);
            return accum;
          }, new Map());

          setFormErrors(errorsMap);
        } else {
          const errorsMap = new Map();
          errorsMap.set(errors.path, errors.errors);

          setFormErrors(errorsMap);
        }

        return false;
      });
  }, []);

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      validate(state.testScore).then(valid => {
        if (valid) {
          setFormErrors(new Map());
          history.push('/signup/step/8');
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      });
    },
    [history, state.testScore, validate]
  );

  const onTestEnabledChange = useCallback(
    (test, enabled) => {
      setState(current => {
        const newState = { ...current };
        newState.testScore[test] = { enabled };

        if (['sat1600', 'sat2400', 'act', 'na'].includes(test)) {
          if (!newState.testScore.enabled) {
            newState.testScore.enabled = [];
          }

          if (!enabled) {
            newState.testScore.enabled = newState.testScore.enabled.filter(
              t => t !== test
            );
          } else {
            newState.testScore.enabled.push(test);
          }
        }

        return newState;
      });
    },
    [setState]
  );

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    validate(state.testScore, true);
  }, [state, validate]);

  return (
    <form onSubmit={onSubmit}>
      <SignupLayout
        title={(
          <span className={'flex justify-between gap-2'}>
            <span>
              Step 7: Test Scores
            </span>
            <span className={'text-small'}>
              Step 7 of 8
            </span>
          </span>
        )}
        footer={
          <>
            <Button
              type={'button'}
              variant={'secondary'}
              size={'xl'}
              icon={ArrowLeftIcon}
              iconProps={{ size: 21 }}
              iconPosition={'left'}
              onClick={() => history.push('/signup/step/6')}
            >
              Back
            </Button>
            <Button
              size={'xl'}
              icon={ArrowRightIcon}
              iconProps={{ size: 21 }}
              iconPosition={'right'}
            >
              Continue
            </Button>
          </>
        }
      >
        <div className={'signup-form-row'}>
          <div className={'signup-form-item signup-form-item--full'}>
            <InputLabel
              contentClassName={'signup-form-row signup-form-row--compact'}
              label={
                'Please share test scores for undergraduate and optionally, graduate admissions.'
              }
              help={
                "If you were part of a cohort that didn't take either the SAT or ACT, select NA for the score."
              }
              errors={formErrors.get('enabled')}
            >
              <div className={'signup-form-item'}>
                <h3>Undergrad</h3>

                <Radio
                  options={[
                    { label: 'SAT (1600)', value: 'sat1600' },
                    { label: 'SAT (2400)', value: 'sat2400' },
                    { label: 'ACT', value: 'act' },
                    { label: 'N/A', value: 'na' },
                  ]}
                  checked={selectedSat}
                  onChange={e => {
                    onTestEnabledChange(
                      'sat1600',
                      e.target.value === 'sat1600'
                    );
                    onTestEnabledChange(
                      'sat2400',
                      e.target.value === 'sat2400'
                    );
                    onTestEnabledChange('act', e.target.value === 'act');
                    onTestEnabledChange('na', e.target.value === 'na');
                  }}
                />
              </div>
              <div className={'signup-form-item signup-form-item--col'}>
                <h3>Grad</h3>

                <Checkbox
                  checked={state?.testScore?.gmat?.enabled || false}
                  label={'GMAT'}
                  onChange={e => {
                    onTestEnabledChange('gmat', e.target.checked);
                  }}
                />
                <Checkbox
                  checked={state?.testScore?.gre?.enabled || false}
                  label={'GRE'}
                  onChange={e => {
                    onTestEnabledChange('gre', e.target.checked);
                  }}
                />
                <Checkbox
                  checked={state?.testScore?.lsat?.enabled || false}
                  label={'LSAT'}
                  onChange={e => {
                    onTestEnabledChange('lsat', e.target.checked);
                  }}
                />
              </div>
            </InputLabel>
          </div>
          {state?.testScore?.sat1600?.enabled && (
            <div
              className={
                'signup-form-row signup-form-row--compact signup-form-item signup-form-item--full'
              }
            >
              <div className={'signup-form-title'}>SAT 1600</div>
              <div className={'signup-form-item'}>
                <Select
                  label={'Math'}
                  options={sat.math}
                  isLoading={isSatLoading}
                  value={state?.testScore?.sat1600?.math}
                  errors={formErrors.get('sat1600.math')}
                  onChange={v => {
                    setState(current => {
                      const newState = { ...current };
                      newState.testScore.sat1600.math = v;
                      return newState;
                    });
                  }}
                />
              </div>
              <div className={'signup-form-item'}>
                <Select
                  label={'Reading and Writing'}
                  options={sat.readingAndWriting}
                  isLoading={isSatLoading}
                  value={state?.testScore?.sat1600?.readingAndWriting}
                  errors={formErrors.get('sat1600.readingAndWriting')}
                  onChange={v => {
                    setState(current => {
                      const newState = { ...current };
                      newState.testScore.sat1600.readingAndWriting = v;
                      return newState;
                    });
                  }}
                />
              </div>
            </div>
          )}
          {state?.testScore?.sat2400?.enabled && (
            <div
              className={
                'signup-form-row signup-form-row--cols-3 signup-form-row--compact signup-form-item signup-form-item--full'
              }
            >
              <div className={'signup-form-title'}>SAT 2400</div>
              <div className={'signup-form-item'}>
                <Select
                  label={'Math'}
                  options={sat.math}
                  isLoading={isSatLoading}
                  value={state?.testScore?.sat2400?.math}
                  errors={formErrors.get('sat2400.math')}
                  onChange={v => {
                    setState(current => {
                      const newState = { ...current };
                      newState.testScore.sat2400.math = v;
                      return newState;
                    });
                  }}
                />
              </div>
              <div className={'signup-form-item'}>
                <Select
                  label={'Reading'}
                  options={sat.reading}
                  isLoading={isSatLoading}
                  value={state?.testScore?.sat2400?.reading}
                  errors={formErrors.get('sat2400.reading')}
                  onChange={v => {
                    setState(current => {
                      const newState = { ...current };
                      newState.testScore.sat2400.reading = v;
                      return newState;
                    });
                  }}
                />
              </div>
              <div className={'signup-form-item'}>
                <Select
                  label={'Writing'}
                  options={sat.writing}
                  isLoading={isSatLoading}
                  value={state?.testScore?.sat2400?.writing}
                  errors={formErrors.get('sat2400.writing')}
                  onChange={v => {
                    setState(current => {
                      const newState = { ...current };
                      newState.testScore.sat2400.writing = v;
                      return newState;
                    });
                  }}
                />
              </div>
            </div>
          )}
          {state?.testScore?.act?.enabled && (
            <div
              className={
                'signup-form-row signup-form-row--compact signup-form-item signup-form-item--full'
              }
            >
              <div className={'signup-form-title'}>ACT</div>
              <div className={'signup-form-item signup-form-item--full'}>
                <Select
                  label={'Overall Score'}
                  options={act}
                  isLoading={isActLoading}
                  value={state?.testScore?.act?.score}
                  errors={formErrors.get('act.score')}
                  onChange={v => {
                    setState(current => {
                      const newState = { ...current };
                      newState.testScore.act.score = v;
                      return newState;
                    });
                  }}
                />
              </div>
            </div>
          )}
          {state?.testScore?.na?.enabled && (
            <div className={'signup-form-item signup-form-item--full'}>
              <TextInput
                label={'Please explain'}
                value={state?.testScore?.na?.reason || ''}
                help={'For example, "Cohort was not required to take SAT/ACT", "International Student", "Don\'t remember score", etc.'}
                error={formErrors.get('na.reason')}
                onChange={v => {
                  setState(current => {
                    const newState = { ...current };
                    newState.testScore.na.reason = v.target.value;
                    return newState;
                  });
                }}
              />
            </div>
          )}
          {state?.testScore?.gmat?.enabled && (
            <div className={'signup-form-item signup-form-item--full'}>
              <Select
                label={'GMAT'}
                options={gmat}
                isLoading={isGmatLoading}
                value={state?.testScore?.gmat?.score}
                errors={formErrors.get('gmat.score')}
                onChange={v => {
                  setState(current => {
                    const newState = { ...current };
                    newState.testScore.gmat.score = v;
                    return newState;
                  });
                }}
              />
            </div>
          )}
          {state?.testScore?.gre?.enabled && (
            <div
              className={
                'signup-form-row signup-form-row--compact signup-form-item signup-form-item--full'
              }
            >
              <div className={'signup-form-title'}>GRE</div>
              <div className={'signup-form-item--col-3'}>
                <Select
                  label={'Verbal Reasoning'}
                  options={gre.verbal}
                  isLoading={isGreLoading}
                  value={state?.testScore?.gre?.verbal}
                  errors={formErrors.get('gre.verbal')}
                  onChange={v => {
                    setState(current => {
                      const newState = { ...current };
                      newState.testScore.gre.verbal = v;
                      return newState;
                    });
                  }}
                />
              </div>
              <div className={'signup-form-item--col-3'}>
                <Select
                  label={'Quantitative Reasoning'}
                  options={gre.quantitative}
                  isLoading={isGreLoading}
                  value={state?.testScore?.gre?.quantitative}
                  errors={formErrors.get('gre.quantitative')}
                  onChange={v => {
                    setState(current => {
                      const newState = { ...current };
                      newState.testScore.gre.quantitative = v;
                      return newState;
                    });
                  }}
                />
              </div>
              <div className={'signup-form-item--col-3'}>
                <Select
                  label={'Analytical Writing'}
                  options={gre.writing}
                  isLoading={isGreLoading}
                  value={state?.testScore?.gre?.analytical}
                  errors={formErrors.get('gre.analytical')}
                  onChange={v => {
                    setState(current => {
                      const newState = { ...current };
                      newState.testScore.gre.analytical = v;
                      return newState;
                    });
                  }}
                />
              </div>
            </div>
          )}
          {state?.testScore?.lsat?.enabled && (
            <div className={'signup-form-item signup-form-item--full'}>
              <Select
                label={'LSAT'}
                options={lsat}
                isLoading={isLsatLoading}
                value={state?.testScore?.lsat?.score}
                errors={formErrors.get('lsat.score')}
                onChange={v => {
                  setState(current => {
                    const newState = { ...current };
                    newState.testScore.lsat.score = v;
                    return newState;
                  });
                }}
              />
            </div>
          )}
          <p>
            We reserve the right to verify test scores. Falsified information
            will result in a lifetime platform ban.
          </p>
        </div>
      </SignupLayout>
    </form>
  );
};

export default SignupStepSeven;
